/*
If 'side-band' or 'side-band-64k' capabilities have been specified by
the client, the server will send the packfile data multiplexed.

Each packet starting with the packet-line length of the amount of data
that follows, followed by a single byte specifying the sideband the
following data is coming in on.

In 'side-band' mode, it will send up to 999 data bytes plus 1 control
code, for a total of up to 1000 bytes in a pkt-line.  In 'side-band-64k'
mode it will send up to 65519 data bytes plus 1 control code, for a
total of up to 65520 bytes in a pkt-line.

The sideband byte will be a '1', '2' or a '3'. Sideband '1' will contain
packfile data, sideband '2' will be used for progress information that the
client will generally print to stderr and sideband '3' is used for error
information.

If no 'side-band' capability was specified, the server will stream the
entire packfile without multiplexing.
*/
import { FIFO } from './FIFO.js';
import { GitPktLine } from './GitPktLine.js';

export class GitSideBand {
  static async demux(input) {
    const read = GitPktLine.streamReader(input);
    const lines = [];

    // And now for the ridiculous side-band or side-band-64k protocol
    const packetlines = new FIFO();
    const packfile = new FIFO();
    const progress = new FIFO();
    // TODO: Use a proper through stream?
    const nextBit = async function () {
      const line = await read();
      // Skip over flush packets
      if (line === null) return await nextBit();
      // A made up convention to signal there's no more to read.
      if (line === true) {
        packetlines.end();
        progress.end();
        packfile.end();
        return;
      }

      // Examine first byte to determine which output "stream" to use
      switch (line[0]) {
        case 1: {
          // pack data
          packfile.write(line.slice(1));
          break;
        }
        case 2: {
          // progress message
          progress.write(line.slice(1));
          lines.push(line.slice(1));
          break;
        }
        case 3: {
          // fatal error message just before stream aborts
          const error = line.slice(1);
          lines.push(error);
          progress.write(error);
          packfile.destroy(new Error(error.toString('utf8')));
          return;
        }
        default: {
          // Not part of the side-band-64k protocol
          packetlines.write(line.slice(0));
        }
      }
      // Careful not to blow up the stack.
      // I think Promises in a tail-call position should be OK.
      if (line.length) {
        await nextBit();
      }
    };
    await nextBit();

    return new TextDecoder().decode(concatUint8Arrays(lines));
  }
}

function concatUint8Arrays(arrays) {
  const length = arrays.reduce((acc, buffer) => acc + buffer.length, 0);
  // Create a new array with total length and merge all source arrays.
  let mergedArray = new Uint8Array(length);
  let offset = 0;
  arrays.forEach((item) => {
    mergedArray.set(item, offset);
    offset += item.length;
  });
  return mergedArray;
}
